// Decides what to do with a survey saved on this device once signal returns.

export type ReplayDecision =
  /** Send this device's copy to the office. */
  | 'upload'
  /** The office already has newer work; drop this device's copy. */
  | 'discard'
  /** Keep this device's copy and ask staff to check with the office. */
  | 'hold';

export interface ReplayContext {
  /** When the survey was last saved on this device (ISO time). */
  localSavedAt: string;
  /**
   * The office copy's last-updated time when this device started working
   * offline. `undefined` for copies saved by older versions of the app, which
   * did not record it.
   */
  baseServerUpdatedAt: string | null | undefined;
  /** The office copy's last-updated time right now, or null if there is none. */
  serverUpdatedAt: string | null;
  /** Status of the office copy right now, e.g. 'draft' or 'completed'. */
  serverStatus: string | null;
}

export function decideReplay(context: ReplayContext): ReplayDecision {
  // No office copy yet: nothing can be overwritten.
  if (!context.serverUpdatedAt) return 'upload';

  const server = Date.parse(context.serverUpdatedAt);

  // The office copy hasn't changed since this device went offline.
  if (context.baseServerUpdatedAt !== undefined) {
    const base = context.baseServerUpdatedAt ? Date.parse(context.baseServerUpdatedAt) : 0;
    if (server <= base) return 'upload';
  }

  // Copies from the previous app version have no starting point recorded. That
  // version kept a copy after every save, so most are duplicates of the office
  // copy. If the office copy is newer, the office has later work: drop the
  // device copy. If the device copy is newer, send it: a duplicate does no
  // harm, and real offline work gets through.
  if (context.baseServerUpdatedAt === undefined) {
    return Date.parse(context.localSavedAt) > server ? 'upload' : 'discard';
  }

  // Someone changed the office copy while this device was offline. Keep both
  // and let staff decide (they can choose "Upload my version anyway").
  return 'hold';
}
